/**
 * Minimal landing analytics tracker for A/B experiments.
 *
 * - Assigns a sticky A/B variant on first visit (localStorage, per-visitor)
 * - Fires `landing_view` once per browser session
 * - Exposes `trackLandingCtaClick()` for CTA buttons/links
 *
 * All calls are fire-and-forget: any failure is silently swallowed so the
 * landing page never breaks because of analytics.
 */
import { useEffect } from "react";
import api from "@/lib/api";

const VARIANT_KEY = "jobgrid_landing_variant";
const VIEW_SENT_KEY = "jobgrid_landing_view_sent";

function readOrAssignVariant() {
  try {
    let v = localStorage.getItem(VARIANT_KEY);
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem(VARIANT_KEY, v);
    }
    return v;
  } catch {
    return "A";
  }
}

export function getLandingVariant() {
  return readOrAssignVariant();
}

function post(event, extra = {}) {
  const variant = readOrAssignVariant();
  const body = { event, variant, meta: extra };
  try {
    // Fire-and-forget — do not await, do not surface failures.
    api.post("/analytics/event", body).catch(() => {});
  } catch { /* noop */ }
}

export function trackLandingCtaClick(label = "") {
  post("landing_cta_click", { label: String(label || "").slice(0, 60) });
}

// -------- Product Tour analytics --------
// Fire-and-forget helpers used by ProductTourModal to measure engagement.
// All calls are silent on failure so the tour keeps playing even if the
// analytics endpoint is offline.
export function trackTourOpened(source = "") {
  post("tour_opened", { source: String(source || "").slice(0, 32) });
}
export function trackTourTabSwitched(clip = "") {
  post("tour_tab_switched", { clip: String(clip || "").slice(0, 24) });
}
export function trackTourClipProgress(clip = "", milestone = 25) {
  post("tour_clip_progress", {
    clip: String(clip || "").slice(0, 24),
    milestone: String(milestone),
  });
}
export function trackTourCompleted(clip = "") {
  post("tour_completed", { clip: String(clip || "").slice(0, 24) });
}
export function trackTourCtaClick(destination = "") {
  post("tour_cta_click", { destination: String(destination || "").slice(0, 24) });
}

export function useLandingViewTracker() {
  useEffect(() => {
    try {
      if (sessionStorage.getItem(VIEW_SENT_KEY) === "1") return;
      sessionStorage.setItem(VIEW_SENT_KEY, "1");
    } catch { /* noop */ }
    post("landing_view");
  }, []);
}
